import { useState } from "react";
import { Inbox, KeyRound, MessageSquare, RefreshCw, ShieldCheck, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInsights() {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/insights?key=${encodeURIComponent(k)}`);
      if (!res.ok) throw new Error("rejected");
      setData(await res.json());
    } catch {
      setError("Wrong key or the server is unreachable. Try again.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-slate-100 px-4 sm:px-8 py-14" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <p className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-3 flex items-center gap-3">
          <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> Visitor Insight Board
        </p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
          Every brief. <span className="text-grad">Every question.</span>
        </h1>

        {!data ? (
          <div className="mt-10 panel rounded-2xl p-8 max-w-md">
            <span className="grid place-items-center w-11 h-11 rounded-xl border border-white/10 bg-white/[0.04] text-neon mb-5">
              <KeyRound size={19} />
            </span>
            <label className="block text-sm text-slate-400 mb-2" htmlFor="admin-key">Private panel — enter the admin key</label>
            <div className="flex gap-2">
              <input
                id="admin-key"
                data-testid="admin-passcode-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load(key)}
                placeholder="Admin key"
                className="flex-1 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm placeholder:text-slate-600 focus:outline-none focus:border-neon/50"
              />
              <button
                data-testid="admin-unlock-button"
                onClick={() => load(key)}
                disabled={loading}
                className="rounded-xl px-5 font-semibold text-ink bg-gradient-to-r from-neon to-vio disabled:opacity-60"
              >
                {loading ? "…" : "Unlock"}
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-rose-400" data-testid="admin-error">{error}</p>}
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <section className="panel rounded-2xl p-6" data-testid="admin-inquiries-panel">
              <div className="flex items-center justify-between mb-5">
                <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
                  <Inbox size={17} className="text-neon" /> Inquiries
                  <span className="text-xs font-mono text-slate-500">({data.inquiries.length})</span>
                </h2>
                <button data-testid="admin-refresh-button" onClick={() => load(key)} className="flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-white transition-colors">
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {data.inquiries.length === 0 && <p className="text-sm text-slate-500">No briefs yet.</p>}
                {data.inquiries.map((q, i) => (
                  <article key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid="admin-inquiry-card">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-100">{q.name} <span className="text-slate-500 font-normal">· {q.email}</span></p>
                      <p className="text-[11px] font-mono text-slate-500">{(q.ts || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                    <p className="mt-1 text-xs text-neon font-mono">{q.scope || "General"}{q.org ? ` · ${q.org}` : ""}</p>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{q.message}</p>
                    <a href={`mailto:${q.email}`} className="mt-3 inline-block text-xs text-slate-400 hover:text-neon transition-colors" data-testid="admin-reply-link">Reply to {q.email}</a>
                  </article>
                ))}
              </div>
            </section>

            <section className="panel rounded-2xl p-6" data-testid="admin-ai-questions-panel">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold mb-5">
                <MessageSquare size={17} className="text-vio" /> AI questions asked
                <span className="text-xs font-mono text-slate-500">({data.ai_questions.length})</span>
              </h2>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                {data.ai_questions.length === 0 && <p className="text-sm text-slate-500">No questions yet.</p>}
                {data.ai_questions.map((q, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5" data-testid="admin-ai-question-item">
                    <p className="text-sm text-slate-200 leading-relaxed">{q.text}</p>
                    <p className="mt-1.5 text-[11px] font-mono text-slate-500">{(q.ts || "").slice(0, 16).replace("T", " ")} · session {String(q.session_id || "").slice(-6)}</p>
                  </div>
                ))}
              </div>
            </section>
            <section className="panel rounded-2xl p-6 lg:col-span-2" data-testid="admin-cv-downloads-panel">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold mb-5">
                <Download size={17} className="text-neon" /> CV downloads
                <span className="text-xs font-mono text-slate-500">({(data.cv_downloads || []).length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {(data.cv_downloads || []).length === 0 && <p className="text-sm text-slate-500">No downloads yet.</p>}
                {(data.cv_downloads || []).map((d, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="admin-cv-download-item">
                    <p className="text-sm font-semibold text-slate-100">{d.name} <span className="text-slate-500 font-normal">· {d.company}</span></p>
                    <p className="mt-1 text-xs text-neon font-mono">{d.email} · {d.phone}</p>
                    <p className="mt-2 text-xs text-slate-400">{d.purpose}</p>
                    <p className="mt-2 text-[11px] font-mono text-slate-500">{(d.ts || "").slice(0, 16).replace("T", " ")} · copy emailed: yes</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        <p className="mt-10 flex items-center gap-2 text-xs font-mono text-slate-600">
          <ShieldCheck size={13} /> Private — served only with the admin key.
        </p>
      </div>
    </div>
  );
}